import { Box, Button, Link as MuiLink, Stack, Typography } from '@mui/material';
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatDoorVisitStatusLabel, UNNAMED_VOTER } from '../voters/voterPresentation';
import { CanvassingSessionTimestamp } from './CanvassingSessionTimestamp';
import {
  MAP_OVERLAY_COPY,
  MAP_OVERLAY_LIST_PAGE_SIZE,
  MAP_OVERLAY_POPUP_PAGE_SIZE,
  type MapOverlayVisit,
  type MapOverlayVoter,
  type MapPopupRecord,
} from './canvassingSessionMapOverlay';

const displayText = (value: string | null, fallback: string): string => value ?? fallback;

export const MapOverlayRecordBody = ({ record }: { record: MapPopupRecord }) => {
  if (record.kind === 'voter') {
    return (
      <Stack spacing={0.25}>
        <Typography variant="body2">{displayText(record.voter.displayName, UNNAMED_VOTER)}</Typography>
        <Typography variant="body2" color="text.secondary">
          {displayText(record.voter.address, MAP_OVERLAY_COPY.addressUnavailable)}
        </Typography>
        <MuiLink component={Link} to={`/voters/${record.voter.id}`}>
          {MAP_OVERLAY_COPY.viewVoter}
        </MuiLink>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.25}>
      <Typography variant="body2">{displayText(record.visit.voterName, MAP_OVERLAY_COPY.unmatchedVisit)}</Typography>
      <Typography variant="body2" color="text.secondary">
        <CanvassingSessionTimestamp timestamp={record.visit.dwellStartedAt} />
      </Typography>
      <Typography variant="body2">{formatDoorVisitStatusLabel(record.visit.status)}</Typography>
      <Typography variant="body2" color="text.secondary">
        {displayText(record.visit.predictedAddress, MAP_OVERLAY_COPY.addressUnavailable)}
      </Typography>
      <MuiLink component={Link} to={`/door-visits/${record.visit.doorVisitId}`}>
        {MAP_OVERLAY_COPY.viewDoorVisit}
      </MuiLink>
    </Stack>
  );
};

const recordKey = (record: MapPopupRecord): string =>
  record.kind === 'voter' ? `voter:${record.voter.id}` : `visit:${record.visit.doorVisitId}`;

export const PagedMapOverlayRecords = ({
  records,
  pageSize,
  label,
}: {
  records: readonly MapPopupRecord[];
  pageSize: number;
  label: string;
}) => {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(records.length / pageSize));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * pageSize;
  const visible = records.slice(start, start + pageSize);

  useEffect(() => {
    setPage(0);
  }, [records]);

  return (
    <Stack spacing={1} aria-label={label}>
      {records.length > pageSize && (
        <Typography variant="caption" color="text.secondary">
          {`${start + 1}–${start + visible.length} of ${records.length}`}
        </Typography>
      )}
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {visible.map(record => (
          <Box component="li" key={recordKey(record)}>
            <MapOverlayRecordBody record={record} />
          </Box>
        ))}
      </Stack>
      {records.length > pageSize && (
        <Stack direction="row" spacing={1}>
          <Button type="button" size="small" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
            {MAP_OVERLAY_COPY.previousRecords}
          </Button>
          <Button type="button" size="small" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>
            {MAP_OVERLAY_COPY.nextRecords}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export const MapOverlayPopup = ({
  records,
  onClose,
}: {
  records: readonly MapPopupRecord[];
  onClose: () => void;
}) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, [records]);

  return (
    <Box
      ref={dialogRef}
      role="dialog"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          onClose();
        }
      }}
      sx={{ maxWidth: 320, outline: 'none' }}
    >
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography id={titleId} variant="subtitle2">{MAP_OVERLAY_COPY.popupLabel}</Typography>
        <Button type="button" size="small" aria-label={MAP_OVERLAY_COPY.closePopup} onClick={onClose}>
          {MAP_OVERLAY_COPY.closePopup}
        </Button>
      </Stack>
      <PagedMapOverlayRecords records={records} pageSize={MAP_OVERLAY_POPUP_PAGE_SIZE} label={MAP_OVERLAY_COPY.popupLabel} />
    </Box>
  );
};

export const MapOverlayRecordList = ({ records }: { records: readonly MapPopupRecord[] }) => {
  const [open, setOpen] = useState(false);
  const panelId = useId();

  return (
    <Stack spacing={1}>
      <Button
        type="button"
        variant="text"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(current => !current)}
        sx={{ alignSelf: 'flex-start' }}
      >
        {open ? MAP_OVERLAY_COPY.hideMapRecords : MAP_OVERLAY_COPY.showMapRecords}
      </Button>
      {open && (
        <Box id={panelId}>
          {records.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{MAP_OVERLAY_COPY.noLocations}</Typography>
          ) : (
            <PagedMapOverlayRecords records={records} pageSize={MAP_OVERLAY_LIST_PAGE_SIZE} label={MAP_OVERLAY_COPY.listLabel} />
          )}
        </Box>
      )}
    </Stack>
  );
};

export const MapOverlayLegend = ({
  showVoters,
  showVisits,
  showMapRecordsLegend = false,
}: {
  showVoters: boolean;
  showVisits: boolean;
  showMapRecordsLegend?: boolean;
}) => {
  if (!showVoters && !showVisits) return null;
  return (
    <Stack spacing={0.5} aria-label="Map overlay legend">
      {showVoters && <LegendSwatch shape="circle" color="success.main" text={MAP_OVERLAY_COPY.voterLegend} />}
      {showVisits && <LegendSwatch shape="square" color="warning.main" text={MAP_OVERLAY_COPY.visitLegend} />}
      {showMapRecordsLegend && (
        <Typography variant="body2" color="text.secondary">{MAP_OVERLAY_COPY.clusterLegend}</Typography>
      )}
    </Stack>
  );
};

const LegendSwatch = ({ shape, color, text }: { shape: 'circle' | 'square'; color: string; text: string }) => (
  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
    <Box
      aria-hidden
      sx={{
        width: shape === 'circle' ? 12 : 14,
        height: shape === 'circle' ? 12 : 14,
        border: '2px solid',
        borderColor: 'common.white',
        bgcolor: color,
        borderRadius: shape === 'circle' ? '50%' : 0.5,
        boxShadow: 1,
      }}
    />
    <Typography variant="body2">{text}</Typography>
  </Stack>
);

export const overlayRecordsInDisplayOrder = (
  voters: readonly MapOverlayVoter[],
  visits: readonly MapOverlayVisit[],
): MapPopupRecord[] => [
  ...voters.map(voter => ({ kind: 'voter' as const, voter })),
  ...visits.map(visit => ({ kind: 'visit' as const, visit })),
];

export const MapOverlayLiveStatus = ({ message }: { message: string }) => {
  if (message.length === 0) return null;

  return (
    <Box aria-live="polite" aria-atomic="true">
      <Typography variant="body2" color="text.secondary">{message}</Typography>
    </Box>
  );
};
